// Which station a quote id opens, and how a case quote's view becomes the
// props of the at-need quote display. The case view carries the same
// `quote` envelope and `{status, plans}` pricing as the policy view, but the
// quote flow's hooks (pricing flags, bootstrap, form clients) must never run
// against it — hence the choice is made from presence, before either
// station mounts.

import type { SavedQuoteResponse } from "components/AtNeedQuote";

import type { CaseQuoteViewResponse } from "./api";

export type QuoteStation = "policy" | "case";

/** A policy unless presence says case; a server that does not say yet
 * answers the policy flow, as before. */
export function pickQuoteStation(
  subjectType: string | null | undefined,
): QuoteStation {
  return subjectType === "case" ? "case" : "policy";
}

/** The at-need quote display's input, shaped from a case view: each plan's
 * single payment carries the engine's own quote, which the display renders the
 * package and its price breakdown from. Absent values the display reads as
 * "none" become undefined, not null. */
export function savedQuoteFromCaseView(
  view: CaseQuoteViewResponse,
): SavedQuoteResponse {
  return {
    quote_id: view.quote.id,
    quote_type: "at_need",
    customer_first_name: view.customer_first_name ?? undefined,
    quote_context: {
      is_v2_market: view.is_v2_market,
      decedent_first_name: view.decedent_first_name ?? undefined,
    },
    packages: view.pricing.plans.flatMap((plan) =>
      plan.payment_options.map((option) => option.snapshot),
    ),
    selected_package: view.selected_package ?? undefined,
    expires_at: null,
    dcd_location: view.dcd_location ?? undefined,
  };
}

/** Whether the view carries packages to show: a case that cannot be priced
 * right now (out of the area, missing a location, the engine down) shows none. */
export function hasPackages(view: CaseQuoteViewResponse): boolean {
  return (
    view.pricing.status === "ok" &&
    view.pricing.plans.some((plan) => plan.payment_options.length > 0)
  );
}

/** The note on a case quote when the family said the loved one pre-planned
 * with After: the prices are the full price, and the plan is confirmed by
 * a person. Null when they did not. */
export function preplannedNote(view: CaseQuoteViewResponse): string | null {
  if (view.preplanned !== true) return null;
  const name = view.decedent_first_name?.trim();
  return `This is our full price. You told us ${name || "your loved one"} pre-planned with After, so your care specialist will look up the plan and confirm your pricing.`;
}

/** The screen a visitor is looking at, as the analytics name it, or null
 * while the loading screen stands in for it: a screen that has not been
 * shown has not been viewed. */
export function screenOnDisplay(
  screen: string,
  waiting: boolean,
): string | null {
  if (waiting || screen === "loading") return null;
  return screen === "ready" ? "quote" : screen;
}

/** The key a screen report is deduped on: the screen, plus the refusal
 * reason when there is one, so a changed reason is a new report. */
export function screenReportKey(
  screen: string | null,
  reason: string | null,
): string | null {
  if (screen === null) return null;
  return reason === null ? screen : `${screen}:${reason}`;
}

/** The key to report now, or null when nothing is on display or this key
 * was already the last one reported. */
export function nextReportKey(
  key: string | null,
  lastReported: string | null,
): string | null {
  if (key === null || key === lastReported) return null;
  return key;
}

export type CaseQuoteShell = "pending" | "portal" | "standalone";

/** Which chrome a case quote renders in. Decided from Firebase's auth state
 * alone, so it is settled before the quote loads and never changes after:
 * a signed-in visitor gets the portal, a visitor holding only a quote
 * credential gets the standalone page. */
export function caseQuoteShell(auth: {
  loading: boolean;
  signedIn: boolean;
}): CaseQuoteShell {
  if (auth.loading) return "pending";
  return auth.signedIn ? "portal" : "standalone";
}
